// usePitchAreaZoom — drag a rectangle on the pitch to zoom into it. A 1:1 port of the
// legacy wireAreaSelect (js/renderers-shotmap.js), shared by Shot Map and Pass Map (both
// build the same rotated `.event-pitch-wrap` shell). The pure halves (clamp, threshold,
// commit rule, overlay box) are in the kit; this hook is only the pointer gesture.
//
// It keeps the legacy's plain DOM flags on the <svg> (04l-d fork 3A) — the modules' own
// hover/click effects read them, so the hook needs no reach into their closures:
// - `dataset.areaPointerDown` from press to release — no hover tooltip can appear while
//   the button is held, even before the drag threshold (a legacy bug fix);
// - `dataset.areaDragging` once past the threshold — the click that ends the drag must not
//   pick a shot.
//
// The dashed overlay is created and removed by hand in `.event-pitch-wrap` (as the legacy):
// it lives only mid-drag and moves on every pointermove, so it's not React state.
//
// The one deviation: the legacy never clears `areaDragging` after a successful zoom — its
// re-render builds a new <svg>, so the flag dies with the old one. React keeps the same
// <svg>, so we clear it on the next frame in BOTH paths (the timing the legacy already uses
// after a too-small drag: after the gesture's own click has fired and been ignored).

import { useEffect, useRef, type RefObject } from "react";
import {
  areaFromDrag,
  areaOverlayBox,
  clampToPitch,
  isAreaDrag,
  type EventArea,
} from "../engines/event-map-kit.js";
import { hideTooltip } from "../canvas/tooltip.js";

export function usePitchAreaZoom(svgRef: RefObject<SVGSVGElement | null>, onArea: (area: EventArea) => void): void {
  // The latest callback, without re-wiring the listeners on every render.
  const onAreaRef = useRef(onArea);
  onAreaRef.current = onArea;

  useEffect(() => {
    const svg = svgRef.current;
    const wrapEl = svg?.closest<HTMLElement>(".event-pitch-wrap");
    if (!svg || !wrapEl) return;
    let dragStart: { x: number; y: number } | null = null;
    let dragging = false;
    let overlayEl: HTMLDivElement | null = null;

    const removeOverlay = () => {
      overlayEl?.remove();
      overlayEl = null;
    };
    // The points live in the rotated group's local space, so its CTM does the mapping.
    const screenCtm = () => (svg.querySelector<SVGGElement>(".pitch-rotate") ?? svg).getScreenCTM();
    const toLocal = (ctm: DOMMatrix, clientX: number, clientY: number) => {
      const pt = svg.createSVGPoint();
      pt.x = clientX;
      pt.y = clientY;
      return pt.matrixTransform(ctm.inverse());
    };
    const toScreen = (ctm: DOMMatrix, p: { x: number; y: number }) => {
      const pt = svg.createSVGPoint();
      pt.x = p.x;
      pt.y = p.y;
      return pt.matrixTransform(ctm);
    };

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      // A tooltip from hovering a shot just before the press would otherwise stay up
      // through the whole drag.
      hideTooltip();
      svg.dataset.areaPointerDown = "1";
      dragStart = { x: e.clientX, y: e.clientY };
      dragging = false;
      // Without capture the browser stops sending move/up once the cursor leaves the svg,
      // so a release past the pitch edge would never land.
      svg.setPointerCapture(e.pointerId);
    };

    const onMove = (e: PointerEvent) => {
      if (!dragStart) return;
      if (!dragging) {
        if (!isAreaDrag(e.clientX - dragStart.x, e.clientY - dragStart.y)) return;
        dragging = true;
        svg.dataset.areaDragging = "1";
        overlayEl = document.createElement("div");
        overlayEl.className = "event-area-select-overlay";
        wrapEl.appendChild(overlayEl);
      }
      const ctm = screenCtm();
      if (!ctm || !overlayEl) return;
      // Clamp both corners to the pitch, then project back to screen: the preview stops at
      // the pitch edge, never implying more pitch than exists.
      const a = toScreen(ctm, clampToPitch(toLocal(ctm, dragStart.x, dragStart.y)));
      const b = toScreen(ctm, clampToPitch(toLocal(ctm, e.clientX, e.clientY)));
      const box = areaOverlayBox(a, b, wrapEl.getBoundingClientRect());
      overlayEl.style.left = `${box.left}px`;
      overlayEl.style.top = `${box.top}px`;
      overlayEl.style.width = `${box.width}px`;
      overlayEl.style.height = `${box.height}px`;
    };

    const onUp = (e: PointerEvent) => {
      if (!dragStart) return;
      // Only gates hover, never the click guard, so it's safe to clear now.
      delete svg.dataset.areaPointerDown;
      const start = dragStart;
      const wasDragging = dragging;
      dragStart = null;
      dragging = false;
      removeOverlay();
      if (!wasDragging) return;
      // Cleared next frame, after this gesture's own click has fired and been ignored.
      requestAnimationFrame(() => {
        delete svg.dataset.areaDragging;
      });
      const ctm = screenCtm();
      if (!ctm) return;
      const area = areaFromDrag(toLocal(ctm, start.x, start.y), toLocal(ctm, e.clientX, e.clientY));
      if (area) onAreaRef.current(area);
    };

    svg.addEventListener("pointerdown", onDown);
    svg.addEventListener("pointermove", onMove);
    svg.addEventListener("pointerup", onUp);
    return () => {
      svg.removeEventListener("pointerdown", onDown);
      svg.removeEventListener("pointermove", onMove);
      svg.removeEventListener("pointerup", onUp);
      removeOverlay();
    };
  }, [svgRef]);
}
